/**
 * useWakeLock — mantiene la pantalla encendida mientras `activo` sea true.
 *
 * POR QUÉ: en el navegador NO existe grabación en segundo plano. Si la
 * pantalla se apaga a mitad de una nota de voz, el sistema suspende la página
 * y el micrófono se corta. El Wake Lock evita el apagado automático durante
 * la grabación (el caso real: hablar varios minutos con el teléfono en la
 * mano o sobre la mesa).
 *
 * Degradación: donde la API no existe (o el sistema la niega, p. ej. batería
 * baja) simplemente no hace nada — la grabación sigue y la red de seguridad
 * es el rescate por `visibilitychange` del componente que graba.
 */
import { useEffect } from 'react';

export function useWakeLock(activo: boolean) {
  useEffect(() => {
    if (!activo || typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;

    let sentinel: WakeLockSentinel | null = null;
    let terminado = false;

    const pedir = async () => {
      try {
        const s = await navigator.wakeLock.request('screen');
        if (terminado) {
          await s.release();
          return;
        }
        sentinel = s;
      } catch {
        /* negado o sin soporte: la grabación no depende de esto */
      }
    };

    // El sistema libera el lock solo al pasar a fondo; al volver, lo repedimos.
    const alVolver = () => {
      if (document.visibilityState === 'visible' && (!sentinel || sentinel.released)) {
        void pedir();
      }
    };

    void pedir();
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      terminado = true;
      document.removeEventListener('visibilitychange', alVolver);
      void sentinel?.release().catch(() => {});
    };
  }, [activo]);
}
